import { regFacilityName } from 'utils/helpers/constVariables';
import { CreateNewObjectInput } from 'generated/graphql';
import { ObjectEntityFragment } from 'pages/facilities/api/fragments/generated/ObjectEntity.fragment';
import { useObjectOperations } from './useObjectOperations';
import { useMemo } from 'react';

export interface FormValues {
  name: string;
  address?: string;
  cityId?: string;
  metroStationIds?: string[];
  timezone?: string;
  naimixLegalEntityId?: string;
  allowInheritance?: boolean;
  showTimesheet: boolean;
  isFinanceActive: boolean;
  showSalaryBeforeModeration: boolean;
  isBonusActive: boolean;
  allowWorkWithoutNaimix: boolean;
  hrmsId?: string;
  checkInMeasurement: number;
  checkOutMeasurement: number;
  avatar?: File;
  coordinates?: {
    lat: number;
    lon: number;
  };
  radius?: number | null;
  parentFacilityId?: string;
  isTimesheetInherited?: boolean;
  addressValue?: {
    value: string;
    data: {
      geo_lat: number;
      geo_lon: number;
    };
  } | null;
}

export const useFacilityPageForm = () => {
  const { object, isLoading, isLoadingCreate, handleSubmit } = useObjectOperations();

  const initialValues: FormValues = useMemo(
    () => ({
      name: object?.name || '',
      address: object?.settings?.address?.addressText || '',
      addressValue: object?.settings?.address
        ? {
            value: object.settings.address.addressText,
            data: {
              geo_lat: object.settings.address.lat,
              geo_lon: object.settings.address.lon,
            },
          }
        : null,
      cityId: object?.settings?.city?.id,
      metroStationIds: object?.settings?.metroStations?.map(station => station.id) || [],
      timezone: object?.settings?.timeZone?.toString(),
      naimixLegalEntityId: object?.settings?.naimixLegalEntity?.id,
      allowInheritance: object?.settings?.isAllowInheritanceFromThisObject || false,
      showTimesheet: object?.settings?.isShowWorkSheetInThisObject || false,
      isFinanceActive: object?.settings?.isDisplayPaymentsWorkers || false,
      showSalaryBeforeModeration: object?.settings?.isDisplayIncomeBeforeModeration || false,
      isBonusActive: object?.settings?.isDisplayBonusesWorkers || false,
      allowWorkWithoutNaimix: object?.settings?.isDefaultAllowWorkWithoutNaimix ?? false,
      hrmsId: object?.parent?.hrmsId ? object.parent.hrmsId.toString() : object?.hrmsId?.toString(),
      checkInMeasurement: object?.settings?.checkInMeasurement || 15,
      checkOutMeasurement: object?.settings?.checkOutMeasurement || 15,
      avatar: undefined,
      coordinates: object?.settings?.address
        ? {
            lat: object.settings.address.lat,
            lon: object.settings.address.lon,
          }
        : undefined,
      radius: object?.settings?.radius,
      parentFacilityId: object?.parent?.id,
      isTimesheetInherited: false,
    }),
    [object],
  );

  const checkTimesheetInheritance = (objects: ObjectEntityFragment[], targetId: string): boolean => {
    const findObjectInHierarchy = (objects: ObjectEntityFragment[], id: string): ObjectEntityFragment | null => {
      for (const obj of objects) {
        if (obj.id === id) return obj;
        if (obj.children) {
          const found = findObjectInHierarchy(obj.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    const findParentInHierarchy = (objects: ObjectEntityFragment[], childId: string): ObjectEntityFragment | null => {
      for (const obj of objects) {
        if (obj.children) {
          for (const child of obj.children) {
            if (child.id === childId) return obj;
            const found = findParentInHierarchy([child], childId);
            if (found) return found;
          }
        }
      }
      return null;
    };

    const targetObject = findObjectInHierarchy(objects, targetId);
    if (!targetObject) return false;

    if (targetObject.settings?.isShowWorkSheetInThisObject) return true;

    let currentObject = targetObject;
    let parent = findParentInHierarchy(objects, currentObject.id);

    while (parent) {
      if (parent.settings?.isShowWorkSheetInThisObject) return true;
      parent = findParentInHierarchy(objects, parent.id);
    }

    return false;
  };

  const validate = (values: FormValues) => {
    const errors: Partial<Record<keyof FormValues, string>> = {};

    if (!values.name?.trim()) {
      errors.name = 'Обязательное поле';
    } else if (!regFacilityName.test(values.name)) {
      errors.name = 'Поле может содержать только буквы русского или латинского алфавитов, цифры, дефис, пробел';
    }

    return errors;
  };

  const onSubmit = (values: FormValues) => {
    const input: CreateNewObjectInput = {
      name: values.name.trim(),
      hrmsId: values.parentFacilityId ? undefined : values.hrmsId ? Number(values.hrmsId) : undefined,
      parentId: values.parentFacilityId,
      settings: {
        checkInMeasurement: values.checkInMeasurement,
        checkOutMeasurement: values.checkOutMeasurement,
        isAllowInheritanceFromThisObject: values.allowInheritance || false,
        isDisplayBonusesWorkers: values.isBonusActive || false,
        isDisplayIncomeBeforeModeration: values.showSalaryBeforeModeration || false,
        isDisplayPaymentsWorkers: values.isFinanceActive || false,
        isShowWorkSheetInThisObject: values.showTimesheet || false,
        isDefaultAllowWorkWithoutNaimix: values.allowWorkWithoutNaimix ?? false,
        cityId: values.cityId,
        naimixLegalEntityId: values.naimixLegalEntityId,
        timeZone: values.timezone ? Number(values.timezone) : undefined,
        ...(values.allowInheritance
          ? {}
          : {
              metroStationIds: values.metroStationIds,
              address:
                values.coordinates && values.address
                  ? {
                      addressText: values.address,
                      lat: Number(values.coordinates.lat),
                      lon: Number(values.coordinates.lon),
                    }
                  : undefined,
              radius: values.radius ? Number(values.radius) : undefined,
            }),
        avatar: values.avatar,
      },
    };

    handleSubmit(input);
  };

  return { initialValues, validate, onSubmit, checkTimesheetInheritance, isLoadingCreate, isLoading };
};
